import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import entete from "../../../assets/img/entete.jpg";
import { formatIttValueForDisplay, frenchDate, frenchDateOnly, getAgeFromSinistre, getPourcentageRepartition, getPrejudiceMoralBeneficiaireKey, getPrixRente, getValeurPointIP, numberOfDaysBetweenDates } from "../../../variables/functions";
import "../../../assets/styles/offre.css";
import axiosInstance from "../../../variables/api/axiosInstance";
import { findPretiumLabel, mapPretiumDolorisOptions, selectApplicableSmig } from "../../../utils/paramHelpers";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";

const PresentationOffreAyantsDroit = React.forwardRef((props, ref) => {
    const tauxResponsabilite = props?.sinistre?.tauxResponsabilite || 100.00;
    const [smig, setSmig] = useState(52500);
    const [pretiumDolorisOptions, setPretiumDolorisOptions] = useState([]);
    const [responsabilite, setResponsabilite] = useState(100);
    const [offre, setOffre] = useState({});
    const [victime, setVictime] = useState({});
    const [incapaciteTemporaire, setIncapaciteTemporaire] = useState(0);
    const [prejudicePhysiologique, setPrejudicePhysiologique] = useState(0);
    const [prejudiceEconomique, setPrejudiceEconomique] = useState(0);
    const [prejudiceMoral, setPrejudiceMoral] = useState(0);
    const [incapacitePermanente, setIncapacitePermanente] = useState(0);
    const [assistanceTiercePersonne, setAssistanceTiercePersonne] = useState(0);
    const [pretiumDoloris, setPretiumDoloris] = useState(0);
    const [prejudiceEsthetique, setPrejudiceEsthetique] = useState(0);
    const [prejudiceScolaire, setPrejudiceScolaire] = useState(0);
    const [prejudiceCarriere, setPrejudiceCarriere] = useState(0);
    const [prejudiceLeses, setPrejudiceLeses] = useState(0);
    const [totalIndemnisation, setTotalIndemnisation] = useState(0);
    const [provisions, setProvisions] = useState([]);
    const [totalProvisions, setTotalProvisions] = useState(0);
    const [revenuACapitaliserDesCoinjoints, setRevenuACapitaliserDesCoinjoints] = useState(0);
    const [revenuACapitaliserDesAscendants, setRevenuACapitaliserDesAscendants] = useState(0);
    const [revenuACapitaliserDesEnfants, setRevenuACapitaliserDesEnfants] = useState(0);
    const [cleDesConjoints, setCleDesCoinjoints] = useState(150);
    const [nbreConjoints, setNbreConjoints] = useState(0);
    const [nbreEnfants, setNbreEnfants] = useState(0);
    const [nbreAscendants, setNbreAscendants] = useState(0);
    const [plafondPrejudiceEconomiqueAD, setPlafondPrejudiceEconomiqueAD] = useState(0);
    const [plafondPrejudiceMoralAD, setPlafondPrejudiceMoralAD] = useState(0);
    const [plafondPrejudiceMoralVeuveAD, setPlafondPrejudiceMoralVeuveAD] = useState(0);
    const [totalPrejudiceEconomique, setTotalPrejudiceEconomique] = useState(0);
    const [totalPrejudiceMoral, setTotalPrejudiceMoral] = useState(0);
    const [tauxIP, setTauxIP] = useState(0);
    const [prixFrancReste, setPrixFrancReste] = useState(0);
    const [expertise, setExpertise] = useState({});
    const [totalPrejudiceMoralVeuveAD, setTotalPrejudiceMoralVeuveAD] = useState(0);
    const [prixRentes, setPrixRentes] = useState({});
    const [prixRentesLoading, setPrixRentesLoading] = useState(false);



    const plafondPe = 10 * smig * 12;

    const isPartnerQualite = (qualite = "") => {
        const upper = (qualite || "").toUpperCase();
        return upper.startsWith("CONJOINT") || upper.startsWith("CONCUBIN");
    };

    useEffect(() => {
        if (!victime?.ayantsDroit?.length) {
            setPrixRentes({});
            return;
        }

        const fetchPrixRentes = async () => {
            setPrixRentesLoading(true);
            try {
                const entries = await Promise.all(
                    victime.ayantsDroit.map(async (ad, index) => {
                        const age = getAgeFromSinistre(ad?.dateNaissance, props?.sinistre?.dateSinistre);
                        const quality = ad?.qualite?.toUpperCase().startsWith("PERE") || ad?.qualite.toUpperCase().startsWith("MERE") ? 100 : ad?.qualite?.startsWith("ENFANT") ? 25 : isPartnerQualite(ad?.qualite) ? 100 : 0;
                        const prix = await getPrixRente(age, ad?.genre, quality);
                        return [index, prix];
                    })
                );

                setPrixRentes(Object.fromEntries(entries));
            } catch (error) {
                console.error("Error fetching prix rentes:", error);
                setPrixRentes({});
            } finally {
                setPrixRentesLoading(false);
            }
        };

        fetchPrixRentes();
    }, [victime?.ayantsDroit]);


    useEffect(() => {
        let isMounted = true;
        axiosInstance
            .get(`/api/pretium-doloris`)
            .then((res) => {
                if (!isMounted) {
                    return;
                }
                setPretiumDolorisOptions(mapPretiumDolorisOptions(res.data));
            })
            .catch((error) => {
                console.error('Erreur lors du chargement des prétium doloris:', error);
                if (isMounted) {
                    setPretiumDolorisOptions([]);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        axiosInstance
            .get(`/api/smig`)
            .then((res) => {
                if (!isMounted) {
                    return;
                }
                const value = selectApplicableSmig(res.data, props?.sinistre);
                if (value) {
                    setSmig(value);
                }
            })
            .catch((error) => {
                console.error('Erreur lors du chargement du SMIG:', error);
            });

        return () => {
            isMounted = false;
        };
    }, [props?.sinistre]);

    useEffect(() => {
        console.log("Offrande: ", props.offre);
        setOffre(props.offre);
        setVictime(props.offre?.victime);

        axiosInstance?.get(`/api/expertise/victime/${props?.offre?.victime?.codeId}`).then((response) => {
            console.log("Expertise: ", response?.data);
            setExpertise(response?.data);
        }).catch((error) => {
            console.log(error);
        });

    }, []);

    useEffect(() => {
        setResponsabilite(props?.sinistre?.tauxResponsabilite ? props?.sinistre?.tauxResponsabilite : 100);

        //Get all provisions
        axiosInstance.get(`/api/decaissement/provisions/${victime?.personne?.codeId}`).then((res) => {
            console.log("Provisions: ", res?.data);
            setProvisions(res?.data);
            let sumProvisions = res?.data?.reduce((a, b) => a + b?.montantGlobal, 0);
            setTotalProvisions(sumProvisions);

        }).catch(err => {
            console.log("err: ", err);
        });

        let age1 = getAgeFromSinistre(victime?.personne?.dateNaissance, props?.sinistre?.dateSinistre);
        let tip = getValeurPointIP(parseInt(expertise?.ipp), age1);
        console.log("Taux IP: ", tip, expertise?.ipp, age1, victime?.personne?.dateNaissance, props?.sinistre?.dateSinistre);
        setTauxIP(tip);
        let pfr = getPrixRente(age1, victime?.personne?.genre, 100);
        setPrixFrancReste(pfr);


        let it = victime?.perceptionSalairePendantTraitement ? 0 : Math.abs(Math.min(
            (
                victime?.revenuProfessionelMensuelAvantAccident * (expertise?.itt - numberOfDaysBetweenDates(props?.sinistre?.dateSinistre, victime?.dateDernierSalaire)) / 30
            ),
            (smig * 6 * 12)
        ));
        setIncapaciteTemporaire(it);
        let pp = 12 * smig * tauxIP * expertise?.ipp / 100;
        setPrejudicePhysiologique(pp);
        let pe = expertise?.ipp >= 50.0 && victime?.perteRevenuProfessionelMensuelApresAccident ? Math.abs(Math.min(
            (
                victime?.perteRevenuProfessionelMensuelApresAccident * 12 * prixFrancReste
            ),
            plafondPe
        )) : 0;
        setPrejudiceEconomique(pe);
        let pm = expertise?.ipp > 80.0 ? 2 * smig * 12 : 0;
        setPrejudiceMoral(pm);
        setIncapacitePermanente(pp + pe + pm);
        let atp = expertise?.ipp > 80.0 && expertise?.besoinAssistance ? (pm + pe + pp) / 2 : 0;
        setAssistanceTiercePersonne(atp);
        let pd = expertise?.pretiumDoloris * smig * 12 / 100;
        setPretiumDoloris(pd);
        let pes = expertise?.prejudiceEsthetique * smig * 12 / 100;
        setPrejudiceEsthetique(pes);
        let ps = victime?.montantBourseMensuelle && expertise?.prejudiceCarriere ? victime?.montantBourseMensuelle * 12 : 0;
        setPrejudiceScolaire(ps);
        let pc = victime?.revenuProfessionelMensuelAvantAccident && expertise?.prejudiceCarriere ? Math.min(victime?.revenuProfessionelMensuelAvantAccident * 6, 36 * smig * 12) : 0;
        setPrejudiceCarriere(pc);
        let pl = expertise?.ipp >= 80.0 ? 2 * smig * 12 : 0;
        setPrejudiceLeses(pl);
        setTotalIndemnisation(pp + pe + pm + it + atp + pd + pes + ps + pc + pl + parseFloat(offre?.montantFraisMedicaux));
        if (victime?.decesSinistre) {
            let nbreCjts = victime?.ayantsDroit?.filter((ad) => isPartnerQualite(ad.qualite)).length;
            setNbreConjoints(nbreCjts);
            let conjointKey = getPourcentageRepartition(victime?.situationFamilialeDefunt, "conjoint");
            let revCapCjt = nbreCjts ? ((victime?.revenuProfessionelMensuelAvantAccident || smig) * conjointKey * 12 / 100) / nbreCjts : 0.0;
            setRevenuACapitaliserDesCoinjoints(revCapCjt);
            setCleDesCoinjoints(conjointKey);
            let nbreAscendants = victime?.ayantsDroit?.filter((ad) => ad.qualite.toUpperCase().startsWith("PERE") || ad.qualite.toUpperCase().startsWith("MERE")).length || 0;
            let ascendantsKey = getPourcentageRepartition(victime?.situationFamilialeDefunt, "ascendant");
            let revCapAscendants = nbreAscendants ? ((victime?.revenuProfessionelMensuelAvantAccident || smig) * ascendantsKey * 12 / 100) / nbreAscendants : 0.0;
            setRevenuACapitaliserDesAscendants(revCapAscendants);
            let nbreOrphelins = victime?.ayantsDroit?.filter((ad) => (ad.qualite.toUpperCase().startsWith("ENFANT MINEUR") || ad.qualite.toUpperCase().startsWith("ENFANT_MINEUR"))).length || 0;
            let orphelinsKey = getPourcentageRepartition(victime?.situationFamilialeDefunt, "enfant");
            let revCapOrphelins = nbreOrphelins ? ((victime?.revenuProfessionelMensuelAvantAccident || smig) * orphelinsKey * 12 / 100) / nbreOrphelins : 0.0;
            setRevenuACapitaliserDesEnfants(revCapOrphelins);
            let tPe = revCapCjt * nbreCjts + revCapAscendants * nbreAscendants + revCapOrphelins * nbreOrphelins;
            setPrejudiceEconomique(tPe);
            setPlafondPrejudiceEconomiqueAD(12 * smig * 85);
            setPlafondPrejudiceMoralAD(smig * 20 * 12);
            setPlafondPrejudiceMoralVeuveAD(smig * 20 * 12);

            let tPmV = 0.0;
            let tpm = 0.0;
            let tot = 0.0;
            victime?.ayantsDroit?.forEach((ad) => {
                let clefPM = getPrejudiceMoralBeneficiaireKey(ad?.qualite) / 100;
                let plaf = isPartnerQualite(ad?.qualite) ? (smig * 6 * 12) : (smig * 20 * 12);
                let pm = Math.min(smig * 12 * clefPM, plaf);
                if (isPartnerQualite(ad.qualite) && ad?.genre?.toUpperCase() === "FEMININ") {
                    tPmV += pm;
                }
                tpm += pm;
            });

            setPrejudiceMoral(tpm);
            setTotalPrejudiceMoralVeuveAD(tPmV);
            let totalPe = 0.0;
            let totalPm = 0.0;
            victime?.ayantsDroit?.forEach((ad) => {
                const quality = ad?.qualite?.toUpperCase().startsWith("PERE") || ad?.qualite.toUpperCase().startsWith("MERE") ? 100 : ad?.qualite?.startsWith("ENFANT") ? 25 : isPartnerQualite(ad?.qualite) ? 100 : 0;
                let prixRente = getPrixRente(getAgeFromSinistre(ad?.dateNaissance, props?.sinistre?.dateSinistre), ad?.genre, quality);
                let rac = (ad?.qualite === "PERE" || ad?.qualite === "MERE") ? revCapAscendants : (ad?.qualite?.startsWith("ENFANT MINEUR") || ad?.qualite?.startsWith("ENFANT_MINEUR")) ? revCapOrphelins : isPartnerQualite(ad?.qualite) ? revCapCjt : 0;
                let clefPM = getPrejudiceMoralBeneficiaireKey(ad?.qualite) / 100;
                let cumulPM = isPartnerQualite(ad?.qualite) && ad?.genre?.toUpperCase() === "FEMININ" ? tPmV : tpm;
                let plaf = isPartnerQualite(ad?.qualite) && ad?.genre?.toUpperCase() === "FEMININ" ? (smig * 6 * 12) : (smig * 20 * 12);
                let plafEco = 12 * smig * 85;
                let pm = cumulPM > plaf ? (plaf * Math.min(smig * 12 * clefPM, plaf) / cumulPM) : Math.min(smig * 12 * clefPM, plaf);
                let pe = Math.min(rac * prixRente, (plafEco * rac * prixRente / tPe));

                tot += rac + pm + pe;
                totalPe += pe;
                totalPm += pm;
            });
            console.log("Total indemnisation: ", tot);
            setTotalIndemnisation(tot);
            setTotalPrejudiceEconomique(totalPe);
            setTotalPrejudiceMoral(totalPm);
        }
        const plafondFraisFuneraires = 2 * smig * 12;
        if (offre?.montantFraisFuneraires > plafondFraisFuneraires) {
            setOffre({ ...offre, montantFraisFuneraires: plafondFraisFuneraires });
        }
    }, [props.expertises, expertise, offre?.montantFraisMedicaux, offre?.montantFraisFuneraires, smig, victime, responsabilite]);

    const expertisePretiumLabel = useMemo(
        () => findPretiumLabel(pretiumDolorisOptions, props?.expertise?.pretiumDoloris),
        [pretiumDolorisOptions, props?.expertise?.pretiumDoloris]
    );
    return (
        <div
            className="ad-print-wrapper"
            ref={ref}
            style={{ display: 'block', overflow: 'visible', height: 'auto' }}
        >            {victime?.ayantsDroit?.map((ayantDroit, index) => {
            const prixRente = prixRentesLoading ? 0 : prixRentes[index];
            let ad = (ayantDroit?.qualite === "PERE" || ayantDroit?.qualite === "MERE") ? "ascendant" : ayantDroit?.qualite?.startsWith("ENFANT") ? ayantDroit?.typeOrphelin === "ORPHELIN DOUBLE" ? "orphelinDouble" : "enfant" : isPartnerQualite(ayantDroit?.qualite) ? "conjoint" : null;
            // console.log(ad, index);
            let clefE = ad ? getPourcentageRepartition(victime?.situationFamilialeDefunt, ad) : 0;
            let clefPM = getPrejudiceMoralBeneficiaireKey(ayantDroit?.qualite) / 100;
            let rac = (ayantDroit?.qualite === "PERE" || ayantDroit?.qualite === "MERE") ? revenuACapitaliserDesAscendants : (ayantDroit?.qualite?.startsWith("ENFANT MINEUR") || ayantDroit?.qualite?.startsWith("ENFANT_MINEUR")) ? revenuACapitaliserDesEnfants : isPartnerQualite(ayantDroit?.qualite) ? revenuACapitaliserDesCoinjoints : 0;
            let cumulPM = isPartnerQualite(ayantDroit?.qualite) && ad?.genre?.toUpperCase() === "FEMININ" ? totalPrejudiceMoralVeuveAD : prejudiceMoral;
            let plaf = isPartnerQualite(ayantDroit?.qualite) && ad?.genre?.toUpperCase() === "FEMININ" ? plafondPrejudiceMoralVeuveAD : plafondPrejudiceMoralAD;
            let pm = cumulPM > plaf ? (plaf * Math.min(smig * 12 * clefPM, plaf) / cumulPM) : Math.min(smig * 12 * clefPM, plaf);
            let pe = prejudiceEconomique > plafondPrejudiceEconomiqueAD ? (plafondPrejudiceEconomiqueAD * rac * prixRente / prejudiceEconomique) : rac * prixRente;
            console.log("DT: plafondPE: ", plafondPrejudiceEconomiqueAD, "prejudiceEconomique: ", prejudiceEconomique, "rac: ", rac, "prixRente: ", prixRente, "pe: ", pe);
            // let tot = rac + pm + pe;
            let tot = pm + pe;
            console.log("Beneficiaire: ", ayantDroit?.prenom, ayantDroit?.nom, "RAC: ", rac, "PM: ", pm, "PE: ", pe, "Total: ", tot);

            return (
                <div className="ad-page-block" key={index}>
                    <div className="ad-page-content">
                        {/* ===== BODY : tout le contenu principal ===== */}
                        <div className="ad-page-body">
                            <header>
                                <img src={entete} alt="En-tête de la société" class="img-fluid header-image" />
                            </header>

                            <h4 class="text-center my-4 data-value">Présentation de l'offre :</h4>
                            <div class="info-section mb-4" style={{ marginBottom: '8px' }}>
                                <h6 class="fw-bold data-value">Informations sur la victime</h6>
                                <div class="row info-line">
                                    <div class="col-4">Nom : <span class="data-value">{props?.offre?.victime?.personne?.nom}</span></div>
                                    <div class="col-4">Prénom : <span class="data-value">{props?.offre?.victime?.personne?.prenom}</span></div>
                                    <div class="col-4">Profession : <span class="data-value">{props?.offre?.victime?.personne?.profession}</span></div>
                                </div>

                                <div class="row info-line">
                                    <div class="col-3">Sexe : <span class="data-value">{props?.offre?.victime?.personne?.genre === "MASCULIN" ? "M" : "F"}</span></div>
                                    <div class="col-4">Date de naissance : <span class="data-value">{frenchDateOnly(props?.offre?.victime?.personne?.dateNaissance)}</span></div>
                                    <div class="col-5">Date de décès : <span class="data-value">{props?.sinistre?.dateSinistre ? frenchDateOnly(props?.sinistre?.dateSinistre) : '-'}</span></div>
                                </div>
                                <div class="row info-line">
                                    <div class="col-4">Revenu :
                                        <span class="data-value">
                                            {props?.offre?.victime?.revenuProfessionelMensuelAvantAccident ? `${displayNumbers(props?.offre?.victime?.revenuProfessionelMensuelAvantAccident)} FCFA` : '-'}
                                        </span>
                                    </div>
                                    <div class="col-4">SMIG :
                                        <span class="data-value">
                                            {smig ? `${displayNumbers(smig)} FCFA` : '-'}
                                        </span>
                                    </div>
                                </div>
                                <div class="row info-line">
                                    <div class="col-4">N° sinistre : <span class="data-value">{props?.sinistre?.numSinistre ? props?.sinistre?.numSinistre : props?.sinistre?.numeroSinistreM}</span></div>
                                    <div class="col-4">Survenu le :
                                        <span class="data-value">
                                            {props?.sinistre?.dateSinistre ? `${frenchDate(props?.sinistre?.dateSinistre)}` : '-'}
                                        </span>
                                    </div>
                                    <div class="col-4">N° Police : <span class="data-value">{props?.sinistre?.contrat?.numeroPoliceM ? props?.sinistre?.contrat?.numeroPoliceM : props?.sinistre?.contrat?.numeroPoliceP}</span></div>
                                </div>
                            </div>
                            <div class="info-section mb-4" style={{ marginBottom: '8px' }}>
                                <h6 class="fw-bold data-value">Informations sur l'ayant droit</h6>
                                <div class="row info-line">
                                    <div class="col-4">Nom : <span class="data-value">{ayantDroit?.nom}</span></div>
                                    <div class="col-4">Prénom : <span class="data-value">{ayantDroit?.prenom}</span></div>
                                    <div class="col-4">Profession : <span class="data-value">{ayantDroit?.profession}</span></div>
                                </div>

                                <div class="row info-line">
                                    <div class="col-3">Sexe : <span class="data-value">{ayantDroit?.genre === "MASCULIN" ? "M" : "F"}</span></div>
                                    <div class="col-4">Date de naissance : <span class="data-value">{frenchDateOnly(ayantDroit?.dateNaissance)}</span></div>
                                </div>
                            </div>

                            <div class="info-section mb-4" style={{ marginBottom: '8px' }}>
                                <h6 class="fw-bold data-value">Offre</h6>
                                <div class="row info-line">
                                    <div class="col-12">Taux de responsabilité du Pool : <span class="data-value">{props?.sinistre?.tauxResponsabilite ? `${props?.sinistre?.tauxResponsabilite}%` : '100%'}</span></div>
                                </div>
                            </div>

                            <div class="table-responsive table-container mb-4">
                                <table class="table table-bordered table-sm table-custom">
                                    <thead class="bg-light text-center">
                                        <tr>
                                            <th rowspan="2" class="align-middle">PREJUDICES INDEMNISABLES</th>
                                            <th colspan="2">MONTANT</th>
                                        </tr>
                                        <tr>
                                            <th>MONTANT SANS PARTAGE <br /> DE RESPONSABILITE</th>
                                            <th>MONTANT APRES PARTAGE <br /> DE RESPONSABILITE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>FRAIS MEDICAUX JUSTIFIES ET AUTRES FRAIS <br /> (Art 258 du code CIMA):</td>
                                            <td>{props?.offre?.montantFraisMedicaux ? displayNumbers(props?.offre?.montantFraisMedicaux) : '0'}</td>
                                            <td>{props?.offre?.montantFraisMedicaux ? displayNumbers(props?.offre?.montantFraisMedicaux * tauxResponsabilite / 100) : '0'}</td>
                                        </tr>
                                        <tr>
                                            <td>FRAIS FUNERAIRES</td>
                                            <td>{props?.offre?.montantFraisFuneraires ? displayNumbers(props?.offre?.montantFraisFuneraires) : '0'}</td>
                                            <td>{props?.offre?.montantFraisFuneraires ? displayNumbers(props?.offre?.montantFraisFuneraires * tauxResponsabilite / 100) : '0'}</td>
                                        </tr>
                                        <tr>
                                            <td colspan="3" class="fw-bold bg-light">INCAPACITE PERMANENTE</td>
                                        </tr>
                                        <tr>
                                            <td class="indent-cell">Préjudice économique</td>
                                            <td>{pe ? displayNumbers(pe) : '0'}</td>
                                            <td>{pe ? displayNumbers(pe * tauxResponsabilite / 100) : '0'}</td>
                                        </tr>
                                        <tr>
                                            <td class="indent-cell">Préjudice moral</td>
                                            <td>{pm ? displayNumbers(pm) : '0'}</td>
                                            <td>{pm ? displayNumbers(pm * tauxResponsabilite / 100) : '0'}</td>
                                        </tr>
                                        <tr class="fw-bold bg-light">
                                            <td>TOTAL DES INDEMNITES</td>
                                            <td>{tot ? displayNumbers(tot) : '0'}</td>
                                            <td>{tot ? displayNumbers(tot * tauxResponsabilite / 100) : '0'}</td>
                                        </tr>
                                        <tr class="fw-bold bg-dark text-white">
                                            <td colspan="2" class="text-center text-white">TOTAL A PAYER</td>
                                            <td className="text-white">{tot ? displayNumbers(tot) : '0'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="row signature-section mt-5">
                                <div class="col-12 text-center">
                                    <p>Fait à Lomé le ...../...../.....</p>
                                    <p class="mt-4">Pour la Société</p>
                                </div>
                            </div>

                        </div>
                        {/* ===== FOOTER : collé en bas de la page ===== */}
                        <div className="ad-page-footer">
                            <DocumentFooter />
                        </div>
                    </div>
                </div>
            )
        })}

        </div>
    );
});

PresentationOffreAyantsDroit.propTypes = {
    offre: PropTypes.object,
    sinistre: PropTypes.object,
};

export default PresentationOffreAyantsDroit;
